import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Download, ShieldCheck, Trash2 } from "lucide-react";
import { LOGO } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "abhi_admin_token";

function Login({ onLogin }) {
  const [form, setForm] = useState({ email: "", password: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, form);
      localStorage.setItem(TOKEN_KEY, data.token);
      onLogin(data.token);
    } catch {
      toast.error("Invalid credentials.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-6">
      <form data-testid="admin-login-form" onSubmit={submit} className="w-full max-w-md border border-brand-line bg-brand-surface p-10 shadow-sm">
        <img src={LOGO} alt="ABHI Institute" className="h-10 w-auto" />
        <h1 className="mt-6 font-serif text-3xl font-light text-brand-ink">Scholarship CRM</h1>
        <p className="mt-1 text-sm text-brand-muted">Admin access only</p>
        <input
          data-testid="admin-email-input"
          type="text"
          required
          placeholder="Username"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          className="mt-8 w-full rounded-md border border-brand-line bg-brand-bg px-4 py-3 text-sm outline-none focus:border-brand-green"
        />
        <input
          data-testid="admin-password-input"
          type="password"
          required
          placeholder="Password"
          value={form.password}
          onChange={(e) => setForm({ ...form, password: e.target.value })}
          className="mt-4 w-full rounded-md border border-brand-line bg-brand-bg px-4 py-3 text-sm outline-none focus:border-brand-green"
        />
        <button
          data-testid="admin-login-button"
          type="submit"
          disabled={sending}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-ink disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          Sign In
        </button>
      </form>
    </div>
  );
}

export default function AdminScholarships() {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY));
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState("scholarships");
  const [admissions, setAdmissions] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/scholarships`, { headers });
      setRows(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Failed to load submissions.");
      }
    }
  };

  const loadAdmissions = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/admissions`, { headers });
      setAdmissions(data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
      } else {
        toast.error("Failed to load admissions.");
      }
    }
  };

  useEffect(() => {
    if (token) {
      load();
      loadAdmissions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const deleteRow = async (type, id, name) => {
    if (!window.confirm(`Delete ${name}'s record permanently? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/${type}/${id}`, { headers });
      if (type === "scholarships") setRows(rows.filter((r) => r.id !== id));
      else setAdmissions(admissions.filter((r) => r.id !== id));
      toast.success("Record deleted.");
    } catch {
      toast.error("Delete failed.");
    }
  };

  const exportCsv = () => {
    const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    let header, lines, name;
    if (tab === "scholarships") {
      header = ["Date", "Name", "Email", "WhatsApp", "City", "Program", "Score", "Scholarship", "Redemption Code", "Status"];
      lines = rows.map((r) =>
        [
          r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "",
          r.name, r.email, r.phone, r.city, r.program,
          r.score !== null && r.score !== undefined ? `${r.score}/20` : "Not taken",
          r.amount ? `₹${r.amount}` : "₹0",
          r.redemption_code || "", r.score !== null && r.score !== undefined ? "Completed" : "Registered",
        ].map(escape).join(",")
      );
      name = "abhi-scholarship-leads";
    } else {
      header = ["Date", "Name", "Email", "Phone", "DOB", "Gender", "Religion", "Course", "Address"];
      lines = admissions.map((r) =>
        [
          r.created_at ? new Date(r.created_at).toLocaleString("en-IN") : "",
          r.name, r.email, r.phone, r.dob, r.gender, r.religion, r.course,
          `${r.address}, ${r.city}, ${r.state} ${r.pincode}`,
        ].map(escape).join(",")
      );
      name = "abhi-admission-leads";
    }
    const blob = new Blob([[header.map(escape).join(","), ...lines].join("\n")], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!token) return <Login onLogin={setToken} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-brand-bg font-sans text-brand-ink">
      <header className="border-b border-brand-line bg-brand-surface">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-4 px-6 py-5">
          <div className="flex items-center gap-4">
            <img src={LOGO} alt="ABHI Institute" className="h-9 w-auto" />
            <div>
              <h1 className="font-serif text-2xl font-light">Admissions CRM</h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-brand-muted">Admin Panel</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              data-testid="csv-export-button"
              onClick={exportCsv}
              disabled={tab === "scholarships" ? !rows?.length : !admissions?.length}
              className="inline-flex items-center gap-2 rounded-full bg-brand-green px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-ink disabled:opacity-50"
            >
              <Download className="h-4 w-4" /> Export Leads to CSV
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={() => { localStorage.removeItem(TOKEN_KEY); setToken(null); }}
              className="inline-flex items-center gap-2 rounded-full border border-brand-line px-5 py-2.5 text-sm font-semibold text-brand-muted transition-colors hover:text-brand-ink"
            >
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8 flex gap-3" data-testid="admin-tabs">
          <button
            data-testid="tab-scholarships"
            onClick={() => setTab("scholarships")}
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${tab === "scholarships" ? "bg-brand-green text-white" : "border border-brand-line text-brand-muted hover:text-brand-ink"}`}
          >
            Scholarship Exams {rows ? `(${rows.length})` : ""}
          </button>
          <button
            data-testid="tab-admissions"
            onClick={() => setTab("admissions")}
            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-colors ${tab === "admissions" ? "bg-brand-green text-white" : "border border-brand-line text-brand-muted hover:text-brand-ink"}`}
          >
            Admission Forms {admissions ? `(${admissions.length})` : ""}
          </button>
        </div>

        {tab === "admissions" ? (
          admissions === null ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
          ) : admissions.length === 0 ? (
            <p data-testid="admissions-empty-state" className="py-20 text-center text-brand-muted">No admission applications yet.</p>
          ) : (
            <div className="overflow-x-auto border border-brand-line bg-brand-surface shadow-sm">
              <table data-testid="admissions-table" className="w-full min-w-[1100px] text-left text-sm">
                <thead>
                  <tr className="border-b border-brand-line font-mono text-[10px] uppercase tracking-[0.14em] text-brand-muted">
                    {["Date", "Candidate", "DOB / Gender / Religion", "Course", "Address", "Documents", "Action"].map((h) => (
                      <th key={h} className="px-4 py-4">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {admissions.map((r, i) => (
                    <tr key={r.id} data-testid={`admission-row-${i}`} className="border-b border-brand-line last:border-0 hover:bg-brand-bg/60">
                      <td className="whitespace-nowrap px-4 py-4 text-brand-muted">
                        {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="px-4 py-4">
                        <p className="font-semibold text-brand-ink">{r.name}</p>
                        <p className="text-xs text-brand-muted">{r.email}</p>
                        <p className="text-xs text-brand-muted">{r.phone}</p>
                      </td>
                      <td className="px-4 py-4 text-xs text-brand-muted">
                        {r.dob} · {r.gender} · {r.religion}
                      </td>
                      <td className="px-4 py-4 font-semibold text-brand-green">{r.course}</td>
                      <td className="max-w-[220px] px-4 py-4 text-xs text-brand-muted">
                        {r.address}, {r.city}, {r.state} — {r.pincode}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          {r.photo && (
                            <a href={r.photo} target="_blank" rel="noreferrer" data-testid={`adm-photo-${i}`}>
                              <img src={r.photo} alt="Applicant" className="h-14 w-14 rounded border border-brand-line object-cover" />
                            </a>
                          )}
                          <div className="flex flex-col gap-1 text-xs font-semibold">
                            {r.cert10 && <a data-testid={`adm-cert10-${i}`} href={r.cert10} target="_blank" rel="noreferrer" className="text-brand-green underline">10th Cert</a>}
                            {r.cert12 && <a data-testid={`adm-cert12-${i}`} href={r.cert12} target="_blank" rel="noreferrer" className="text-brand-green underline">12th Cert</a>}
                            {r.id_proof && <a href={r.id_proof} target="_blank" rel="noreferrer" className="text-brand-green underline">ID Proof</a>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <button
                          data-testid={`admission-delete-${i}`}
                          onClick={() => deleteRow("admissions", r.id, r.name)}
                          title="Delete this application"
                          className="rounded-full border border-red-200 p-2.5 text-red-500 transition-colors hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : rows === null ? (
          <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-brand-green" /></div>
        ) : rows.length === 0 ? (
          <p data-testid="admin-empty-state" className="py-20 text-center text-brand-muted">No scholarship submissions yet.</p>
        ) : (
          <div className="overflow-x-auto border border-brand-line bg-brand-surface shadow-sm">
            <table data-testid="admin-table" className="w-full min-w-[1000px] text-left text-sm">
              <thead>
                <tr className="border-b border-brand-line font-mono text-[10px] uppercase tracking-[0.14em] text-brand-muted">
                  {["Date", "Candidate", "Program", "Score", "Scholarship", "Code", "Status", "Action"].map((h) => (
                    <th key={h} className="px-4 py-4">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.id} data-testid={`admin-row-${i}`} className="border-b border-brand-line last:border-0 hover:bg-brand-bg/60">
                    <td className="whitespace-nowrap px-4 py-4 text-brand-muted">
                      {r.created_at ? new Date(r.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    </td>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-brand-ink">{r.name}</p>
                      <p className="text-xs text-brand-muted">{r.email}</p>
                      <p className="text-xs text-brand-muted">{r.phone} · {r.city}</p>
                    </td>
                    <td className="px-4 py-4 text-brand-muted">{r.program}</td>
                    <td className="px-4 py-4 font-serif text-lg text-brand-ink">
                      {r.score !== null && r.score !== undefined ? `${r.score}/20` : "—"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-4 font-semibold text-brand-green">
                      {r.amount ? `₹${r.amount.toLocaleString("en-IN")}` : r.score !== null ? "₹0" : "—"}
                    </td>
                    <td className="px-4 py-4 font-mono text-xs text-brand-ink">{r.redemption_code || "—"}</td>
                    <td className="px-4 py-4">
                      <span
                        data-testid={`exam-status-${i}`}
                        className={`inline-block whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-semibold ${
                          r.score !== null && r.score !== undefined
                            ? "border-green-200 bg-green-50 text-green-700"
                            : "border-amber-200 bg-amber-50 text-amber-700"
                        }`}
                      >
                        {r.score !== null && r.score !== undefined ? "Exam Completed" : "Registered"}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <button
                        data-testid={`scholarship-delete-${i}`}
                        onClick={() => deleteRow("scholarships", r.id, r.name)}
                        title="Delete this record"
                        className="rounded-full border border-red-200 p-2.5 text-red-500 transition-colors hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
